import { Button, Form, Input, notification, Select } from "antd";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { serviceTypeListAsync } from "../../../../features/serviceType/serviceTypeListSlice";
import {
  associateServiceToProvider,
  updateProviderService,
} from "../../../../services/providerService";
import { ProviderServiceStatus } from "../../../../shared/constants/ProviderServiceStatus";
import {
  ProviderServiceStatusDescription,
  ServiceModelDescription,
  ServiceNumberDescription,
  ServiceTypeIdDescription,
} from "../../../../shared/constants/Resources";
import { DataType } from "../types";

interface DriverServiceFormProps {
  driverId: number;
  onSuccess: () => void;
  providerService?: DataType | undefined;
}

const DriverServiceForm = ({
  driverId,
  onSuccess,
  providerService,
}: DriverServiceFormProps) => {
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState([] as any);
  const [saving, setSaving] = useState(false);
  const { value: serviceTypes, status: loading } = useAppSelector(
    (x) => x.serviceTypeList,
  );

  const [form] = Form.useForm();

  const getServiceTypesData = async () => {
    setStatus(ProviderServiceStatus);

    if (serviceTypes.length) return;

    await dispatch(serviceTypeListAsync());
  };

  useEffect(() => {
    getServiceTypesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = async (values: any) => {
    const { serviceNumber, serviceModel } = values;
    const serviceTypeIdValue = values[ServiceTypeIdDescription];

    try {
      setSaving(true);
      if (providerService) {
        const { status } = values;
        await updateProviderService({
          id: providerService.id,
          providerId: driverId,
          serviceTypeId: serviceTypeIdValue,
          serviceNumber,
          serviceModel,
          status,
        });
      } else {
        await associateServiceToProvider({
          providerId: driverId,
          serviceTypeIds: serviceTypeIdValue,
          serviceNumber,
          serviceModel,
        });
      }

      notification.success({
        message: `Servicio ${providerService ? "actualizado" : "creado"
          } correctamente!`,
        placement: "bottom",
        key: "global",
      });

      form.resetFields();

      setSaving(false);

      onSuccess();
    } catch (error) {
      notification.error({
        message: `Ha ocurrido un error al ${providerService ? "actualizar" : "asociar"
          } el servicio.`,
        placement: "bottom",
        key: "global",
      });
    }
  };

  return (
    <Form
      form={form}
      name="basic"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      onFinish={onFinish}
      autoComplete="off"
      fields={
        providerService
          ? [
            {
              name: [ServiceTypeIdDescription],
              value: providerService?.serviceTypeId,
            },
            {
              name: [ServiceNumberDescription],
              value: providerService?.serviceNumber,
            },
            {
              name: [ServiceModelDescription],
              value: providerService?.serviceModel,
            },
            {
              name: [ProviderServiceStatusDescription],
              value: providerService?.status,
            },
          ]
          : []
      }
    >
      <Form.Item
        label="Tipo de servicio"
        name={ServiceTypeIdDescription}
        rules={[
          {
            required: true,
            message: "Por favor seleccione un tipo de servicio!",
          },
        ]}
      >
        <Select
          mode={providerService ? undefined : "multiple"}
          disabled={providerService !== undefined}
          loading={loading === "loading"}
          optionFilterProp="label"
          showSearch
          options={serviceTypes.map((x) => ({ value: x.id, label: x.name }))}
        />
      </Form.Item>

      <Form.Item
        label="Número de servicio"
        name={ServiceNumberDescription}
        rules={[{ required: true, message: "Por favor ingresa un número!" }]}
      >
        <Input placeholder="Ej: CY98769" />
      </Form.Item>

      <Form.Item
        label="Modelo"
        name="serviceModel"
        rules={[{ required: true, message: "Por favor ingresa un modelo!" }]}
      >
        <Input placeholder="Ej: Audi A8" />
      </Form.Item>

      {providerService && (
        <Form.Item
          label="Status del servicio"
          name="status"
          rules={[
            {
              required: true,
              message: "Por favor seleccione un status!",
            },
          ]}
        >
          <Select
            loading={loading === "loading"}
            options={status}
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>
      )}

      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={loading === "loading"}
          loading={saving}
        >
          Guardar
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DriverServiceForm;
